import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { ArrowsClockwise, X, CheckCircle } from "@phosphor-icons/react";

const KEY = "iyeneh_autotopup_nudge_dismissed";

// Invites customers who've sent a one-off top-up (and have no schedule yet)
// to switch on a monthly auto top-up — recurring margin on autopilot.
export function AutoTopUpNudge() {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(KEY) === "1");
  const { data: history } = useQuery({ queryKey: ["topup-history"], queryFn: async () => (await api.get("/topup/history")).data });
  const { data: schedules } = useQuery({ queryKey: ["topup-schedules"], queryFn: async () => (await api.get("/topup/schedules")).data });

  const sent = history?.topups?.length || 0;
  const active = schedules?.schedules?.length || 0;
  if (dismissed || sent === 0 || active > 0) return null;

  const lastRecipient = history.topups[0]?.recipient;
  const close = () => { localStorage.setItem(KEY, "1"); setDismissed(true); };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-accent/60 to-card p-5" data-testid="auto-topup-nudge">
      <button data-testid="nudge-dismiss" onClick={close} className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"><X size={16} /></button>
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground"><ArrowsClockwise size={22} weight="duotone" /></div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg font-semibold">Put your top-ups on autopilot</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            You've sent {sent} top-up{sent > 1 ? "s" : ""}. Set up a <span className="font-semibold text-foreground">monthly auto top-up</span>
            {lastRecipient ? <> for <span className="font-mono-metric">{lastRecipient}</span></> : ""} so they never run out — you'll never have to remember.
          </p>
          <ul className="mt-2 space-y-1">
            {["Sends automatically every month", "Cancel anytime", "Low-balance reminders keep it running"].map((f) => (
              <li key={f} className="flex items-center gap-1.5 text-xs text-muted-foreground"><CheckCircle size={13} weight="fill" className="text-primary" /> {f}</li>
            ))}
          </ul>
          <button data-testid="nudge-setup" onClick={() => navigate("/app/topup")}
            className="mt-3 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]">Set up monthly auto top-up</button>
        </div>
      </div>
    </div>
  );
}
